import React, { useEffect, useRef, useState } from 'react';

import { Button } from 'primereact/button';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { InputText } from 'primereact/inputtext';
import { Toast } from 'primereact/toast';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';

import { BlocManagementNews } from '../../../bloc/BlocManagementNews';
import { News, NewsViewState, CreateNewsDto, UpdateNewsDto } from '../../../../types/news.types';
import { NewsDialog } from '../../../components/news/NewsDialog';
import AdminProtectedRoute from '../../../components/admin/AdminProtectedRoute';
import AdminLayout from '../../../components/admin/AdminLayout';
import StreamBuilder from '../../../components/common/StreamBuilder';

export const NewsManagementPage: React.FC = () => {
    // Bloc instance
    const [bloc] = useState(() => new BlocManagementNews());

    // UI state
    const [globalFilter, setGlobalFilter] = useState<string>('');
    const [dialogVisible, setDialogVisible] = useState<boolean>(false);
    const [selectedNews, setSelectedNews] = useState<News | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(false);

    // Refs
    const toast = useRef<Toast>(null);
    const dt = useRef<DataTable<News[]>>(null);

    // Load data on component mount
    useEffect(() => {
        bloc.initData();

        // Cleanup on unmount
        return () => {
            bloc.dispose();
        };
    }, [bloc]);

    // Toast messages
    const showToast = (severity: 'success' | 'error', summary: string, detail: string) => {
        toast.current?.show({
            severity,
            summary,
            detail,
            life: 3000
        });
    };

    // Handle news operations
    const handleCreateNews = () => {
        setSelectedNews(null);
        setDialogVisible(true);
    };

    const handleEditNews = (news: News) => {
        setSelectedNews(news);
        setDialogVisible(true);
    };

    const handleDeleteNews = (news: News) => {
        confirmDialog({
            message: `Bạn có chắc chắn muốn xóa tin tức này?`,
            header: 'Xác nhận xóa',
            icon: 'pi pi-exclamation-triangle',
            acceptClassName: 'p-button-danger',
            accept: async () => {
                try {
                    setIsLoading(true);
                    await bloc.deleteNews(news.id);
                    showToast('success', 'Thành công', 'Xóa tin tức thành công');
                } catch (error) {
                    showToast('error', 'Lỗi', 'Không thể xóa tin tức');
                } finally {
                    setIsLoading(false);
                }
            }
        });
    };

    const handleSaveNews = async (data: CreateNewsDto | UpdateNewsDto) => {
        try {
            setIsLoading(true);

            if (selectedNews) {
                await bloc.updateNews(selectedNews.id, data as UpdateNewsDto);
                showToast('success', 'Thành công', 'Cập nhật tin tức thành công');
            } else {
                await bloc.createNews(data as CreateNewsDto);
                showToast('success', 'Thành công', 'Thêm mới tin tức thành công');
            }

            setDialogVisible(false);
        } catch (error) {
            showToast('error', 'Lỗi', 'Không thể lưu tin tức');
            // console.error(error);
        } finally {
            setIsLoading(false);
        }
    };

    // Handle refresh
    const handleRefresh = () => {
        bloc.refresh();
    };

    // Handle pagination
    const onPageChange = async (event: any) => {
        if (event.rows !== bloc.currentPagination.limit) {
            await bloc.changePageSize(event.rows);
        } else {
            await bloc.changePage(event.page + 1);
        }
    };

    // Table templates
    const indexTemplate = (news: News) => {
        return <span>{news.index || '-'}</span>;
    };

    const titleTemplate = (news: News) => {
        return (
            <div>
                <div className="font-bold">{news.title}</div>
                {news.content && (
                    <div className="text-sm text-gray-500 mt-1">
                        {news.content.length > 50
                            ? news.content.substring(0, 50) + '...'
                            : news.content}
                    </div>
                )}
            </div>
        );
    };

    const imageTemplate = (news: News) => {
        return (
            <img
                src={news.url_image}
                alt="News"
                style={{ height: '50px', maxWidth: '100px', objectFit: 'contain' }}
                onError={(e) => (e.currentTarget.src = 'https://via.placeholder.com/100x50?text=Error')}
            />
        );
    };

    const linkTemplate = (news: News) => {
        return (
            <a
                href={news.url_banner}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-500 hover:text-blue-700"
                title={news.url_banner}
            >
                {news.url_banner.length > 30
                    ? news.url_banner.substring(0, 30) + '...'
                    : news.url_banner}
            </a>
        );
    };

    const dateTemplate = (news: News) => {
        return (
            <div className="text-sm">
                <div>Tạo: {new Date(news.created_at).toLocaleString('vi-VN')}</div>
                <div>Cập nhật: {new Date(news.updated_at).toLocaleString('vi-VN')}</div>
            </div>
        );
    };

    const actionTemplate = (news: News) => {
        return (
            <div className="flex gap-2">
                <Button
                    icon="pi pi-pencil"
                    className="p-button-rounded p-button-success p-button-text"
                    onClick={() => handleEditNews(news)}
                    tooltip="Chỉnh sửa"
                />
                <Button
                    icon="pi pi-trash"
                    className="p-button-rounded p-button-danger p-button-text"
                    onClick={() => handleDeleteNews(news)}
                    tooltip="Xóa"
                />
            </div>
        );
    };

    // Header template with search and add button
    const renderHeader = () => {
        return (
            <div className="flex flex-wrap justify-content-between align-items-center">
                <span className="p-input-icon-left">
                    <i className="pi pi-search" />
                    <InputText
                        value={globalFilter}
                        onChange={(e) => setGlobalFilter(e.target.value)}
                        placeholder="Tìm kiếm..."
                    />
                </span>
                <div>
                    <Button
                        icon="pi pi-refresh"
                        className="p-button-outlined mr-2"
                        onClick={handleRefresh}
                        tooltip="Làm mới"
                    />
                    <Button
                        icon="pi pi-plus"
                        className="p-button"
                        label="Thêm mới"
                        onClick={handleCreateNews}
                    />
                </div>
            </div>
        );
    };

    return (
        <AdminProtectedRoute
            requiredModule="news-banners-management"
            requiredSubModule="news_management"
            requiredPermission="view"
        >
            <AdminLayout>
                <div className="card">
                    <Toast ref={toast} />
                    <ConfirmDialog />

                    <StreamBuilder
                        stream={bloc.getStream('info')}
                        initialData={{
                            news: [],
                            pagination: { total: 0, page: 1, limit: 10, totalPages: 0 },
                            loading: true,
                            error: null,
                            filters: {},
                            hasData: false
                        }}
                        builder={(snapshot: any) => {
                            const state: NewsViewState = snapshot.data;

                            return (
                                <div>
                                    <DataTable
                                        ref={dt}
                                        value={state.news}
                                        scrollHeight='calc(100vh - 200px)'
                                        paginator
                                        rows={state.pagination.limit}
                                        totalRecords={state.pagination.total}
                                        lazy
                                        first={(state.pagination.page - 1) * state.pagination.limit}
                                        onPage={onPageChange}
                                        rowsPerPageOptions={[5, 10, 25, 50]}
                                        paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                                        currentPageReportTemplate="{first} - {last} của {totalRecords} tin tức"
                                        loading={state.loading}
                                        header={renderHeader()}
                                        globalFilter={globalFilter}
                                        emptyMessage="Không tìm thấy tin tức nào"
                                        showGridlines
                                        dataKey="id"
                                        stripedRows
                                    >
                                        <Column field="index" header="Thứ tự" body={indexTemplate} style={{ minWidth: '80px', textAlign: 'center' }} />
                                        <Column field="title" header="Tiêu đề" body={titleTemplate} style={{ minWidth: '250px' }} />
                                        <Column field="url_image" header="Hình ảnh" body={imageTemplate} style={{ minWidth: '120px', textAlign: 'center' }} />
                                        <Column field="url_banner" header="Liên kết" body={linkTemplate} style={{ minWidth: '200px', textAlign: 'center' }} />
                                        <Column field="created_at" header="Thời gian" body={dateTemplate} style={{ minWidth: '200px', textAlign: 'center' }} />
                                        <Column field="action" header="Thao tác" body={actionTemplate} style={{ minWidth: '120px', textAlign: 'center' }} />
                                    </DataTable>

                                    <NewsDialog
                                        visible={dialogVisible}
                                        news={selectedNews}
                                        onHide={() => setDialogVisible(false)}
                                        onSave={handleSaveNews}
                                        isLoading={isLoading}
                                        title={selectedNews ? 'Chỉnh sửa Tin tức' : 'Thêm mới Tin tức'}
                                    />
                                </div>
                            );
                        }}
                    />
                </div>
            </AdminLayout>
        </AdminProtectedRoute>
    );
};
